import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  FaCheckCircle,
  FaDownload,
  FaFileInvoice,
  FaPassport,
  FaShieldAlt,
  FaSpinner,
  FaWhatsapp,
  FaCar,
} from 'react-icons/fa';
import bridgeCollectionService from '../../services/bridgeCollectionApi';

const formatINR = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(
    Number(n) || 0
  );

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const PAGE_SHELL = 'min-h-[100dvh] min-h-screen bg-slate-50';
const PAGE_CONTAINER = 'w-full max-w-lg sm:max-w-xl md:max-w-2xl lg:max-w-3xl mx-auto px-4 sm:px-6';
const PAGE_HEADER = 'sticky top-0 z-20 bg-white/95 backdrop-blur-sm border-b border-slate-200 px-4 sm:px-6 py-3 sm:py-4';
const CARD = 'rounded-2xl bg-white border border-slate-200 p-4 sm:p-5 shadow-sm';
const META_ROW = 'flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between text-sm min-w-0';
const FIXED_FOOTER =
  'fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur-sm border-t border-slate-200 px-4 sm:px-6 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]';
const FIXED_FOOTER_INNER = `${PAGE_CONTAINER} !px-0 space-y-2`;

const METHOD_LABELS = {
  upi: 'UPI',
  card: 'Card',
  cash: 'Cash',
  netbanking: 'Net Banking',
};

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

/**
 * CV-04 / CV-05 — Customer Bridge collection + invoice (no login).
 * Route: /bridge/collection/:token
 */
const BridgeCollection = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [collection, setCollection] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [step, setStep] = useState('collection');
  const [paymentMethod, setPaymentMethod] = useState('upi');
  const [payLoading, setPayLoading] = useState(false);
  const [deliveryOtp, setDeliveryOtp] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const idempotencyKey = useMemo(
    () => (collection?.jobCardId ? `bridge-labour-${collection.jobCardId}` : ''),
    [collection?.jobCardId]
  );

  const loadInvoice = useCallback(async () => {
    const inv = await bridgeCollectionService.getInvoice(token);
    setInvoice(inv);
    return inv;
  }, [token]);

  const loadCollection = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const data = await bridgeCollectionService.getCollection(token);
      setCollection(data);

      if (data.labourPaid) {
        if (data.deliveryOtp) {
          setDeliveryOtp(data.deliveryOtp);
          setStep('otp');
        } else {
          await loadInvoice();
          setStep('invoice');
        }
      } else {
        const methods = data.paymentMethods || ['upi', 'card'];
        setPaymentMethod(methods[0] || 'upi');
        setStep('collection');
      }
    } catch (err) {
      if (err.status === 401 || /expired/i.test(err.message || '')) {
        setStep('expired');
      } else {
        setError(err.message || 'Unable to load collection link');
      }
    } finally {
      setLoading(false);
    }
  }, [token, loadInvoice]);

  useEffect(() => {
    loadCollection();
  }, [loadCollection]);

  const availableMethods = useMemo(() => {
    const raw = collection?.paymentMethods || ['upi', 'card', 'cash'];
    return raw.filter((m) => METHOD_LABELS[m]);
  }, [collection]);

  const cashSupported = availableMethods.includes('cash');
  const onlineMethods = availableMethods.filter((m) => m !== 'cash');

  const garageLabel = collection?.garageCity ? `${collection.garageCity}` : 'Your';
  const preview = collection?.invoicePreview || {};

  const handlePayLabour = async () => {
    if (!token || !idempotencyKey || paymentMethod === 'cash') return;
    setPayLoading(true);
    setError('');
    try {
      const paymentData = await bridgeCollectionService.initiateLabourPayment(token, {
        idempotencyKey,
        method: paymentMethod,
      });
      const { keyId, orderId, amountPaise, amount, currency } = paymentData;

      await loadRazorpayScript();

      if (keyId && orderId && window.Razorpay) {
        const options = {
          key: keyId || process.env.REACT_APP_RAZORPAY_KEY_ID,
          amount: amountPaise || Math.round((amount || 0) * 100),
          currency: currency || 'INR',
          name: 'SparePe',
          description: `Labour — Job ${collection?.jobCardId || ''}`,
          order_id: orderId,
          method: paymentMethod === 'upi' ? { upi: true } : undefined,
          handler: async (res) => {
            try {
              const result = await bridgeCollectionService.verifyPayment(token, {
                razorpay_order_id: res.razorpay_order_id,
                razorpay_payment_id: res.razorpay_payment_id,
                razorpay_signature: res.razorpay_signature,
              });
              setDeliveryOtp(result.deliveryOtp);
              setStep('otp');
            } catch (verifyErr) {
              setError(verifyErr.message || 'Payment verification failed');
            } finally {
              setPayLoading(false);
            }
          },
          modal: { ondismiss: () => setPayLoading(false) },
        };
        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', () => {
          setPayLoading(false);
          setError('Payment failed or was cancelled.');
        });
        rzp.open();
        return;
      }

      const demoPaymentId = `pay_demo_${Date.now()}`;
      const demoOrderId = orderId || `order_${Date.now()}`;
      let demoSig = `demo_${demoOrderId}_${demoPaymentId}`;
      try {
        const demoRes = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/payment/demo-signature`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              razorpay_order_id: demoOrderId,
              razorpay_payment_id: demoPaymentId,
            }),
          }
        );
        const demoBody = await demoRes.json();
        if (demoBody?.data?.signature) demoSig = demoBody.data.signature;
      } catch (_) {
        /* demo fallback */
      }

      const result = await bridgeCollectionService.verifyPayment(token, {
        razorpay_order_id: demoOrderId,
        razorpay_payment_id: demoPaymentId,
        razorpay_signature: demoSig,
      });
      setDeliveryOtp(result.deliveryOtp);
      setStep('otp');
    } catch (err) {
      setError(err.message || 'Payment could not be started');
    } finally {
      setPayLoading(false);
    }
  };

  const handleCashAtGarage = async () => {
    setPayLoading(true);
    setError('');
    try {
      const result = await bridgeCollectionService.markCashAtGarage(token);
      setDeliveryOtp(result.deliveryOtp);
      setStep('otp');
    } catch (err) {
      setError(err.message || 'Could not record cash payment');
    } finally {
      setPayLoading(false);
    }
  };

  const handleViewInvoice = async () => {
    setPayLoading(true);
    setError('');
    try {
      await loadInvoice();
      setStep('invoice');
    } catch (err) {
      setError(err.message || 'Could not load invoice');
    } finally {
      setPayLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    setError('');
    try {
      await bridgeCollectionService.downloadInvoicePdf(
        token,
        `invoice-${invoice?.jobCardId || collection?.jobCardId || 'service'}.pdf`
      );
    } catch (err) {
      setError(err.message || 'Could not download invoice');
    } finally {
      setPdfLoading(false);
    }
  };

  const whatsAppShareUrl = useMemo(() => {
    const inv = invoice || collection;
    if (!inv) return 'https://wa.me/';
    const msg = encodeURIComponent(
      `Service invoice — Job ${inv.jobCardId || ''} at ${inv.garageName || 'garage'}. Total paid: ${formatINR(invoice?.totalPaid ?? collection?.labourAmount ?? 0)}.`
    );
    return `https://wa.me/?text=${msg}`;
  }, [invoice, collection]);

  if (loading) {
    return (
      <div className={`${PAGE_SHELL} flex items-center justify-center p-6`}>
        <div className="flex items-center gap-2 text-slate-600 text-sm sm:text-base">
          <FaSpinner className="animate-spin" /> Loading…
        </div>
      </div>
    );
  }

  if (step === 'expired') {
    return (
      <div className={`${PAGE_SHELL} flex items-center justify-center p-4 sm:p-6`}>
        <div className={`${CARD} max-w-md w-full p-6 sm:p-8 text-center`}>
          <p className="text-lg font-semibold text-slate-900 mb-2">Link expired</p>
          <p className="text-slate-600 text-sm">
            This link has expired. Contact your garage for a new link.
          </p>
        </div>
      </div>
    );
  }

  /* CV-05 — Full invoice + passport + warranties */
  if (step === 'invoice' && invoice) {
    return (
      <div className={`${PAGE_SHELL} pb-8`}>
        <header className={PAGE_HEADER}>
          <div className={PAGE_CONTAINER}>
            <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wide">SparePe</p>
            <h1 className="text-base sm:text-lg font-bold text-slate-900 mt-0.5">Service invoice</h1>
          </div>
        </header>

        <main className={`${PAGE_CONTAINER} py-4 sm:py-6 space-y-4`}>
          {error && (
            <div className="rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm px-4 py-3">{error}</div>
          )}

          <div className={`${CARD} space-y-3`}>
            <div className={META_ROW}>
              <span className="text-slate-500 shrink-0">Job card</span>
              <span className="font-mono font-semibold text-slate-900 break-all text-right sm:text-left">{invoice.jobCardId}</span>
            </div>
            <div className={META_ROW}>
              <span className="text-slate-500">Date</span>
              <span className="text-slate-900">{formatDate(invoice.invoiceDate)}</span>
            </div>
            <div className={META_ROW}>
              <span className="text-slate-500 shrink-0">Garage</span>
              <span className="text-slate-900 text-right sm:text-left break-words">
                {invoice.garageName}, {invoice.garageCity}
              </span>
            </div>
            <div className="flex items-start sm:items-center gap-2 text-sm pt-2 border-t border-slate-100 min-w-0">
              <FaCar className="text-slate-400 shrink-0 mt-0.5 sm:mt-0" />
              <span className="text-slate-900 break-words">
                {invoice.vehicle?.registrationNumber}{' '}
                {[invoice.vehicle?.make, invoice.vehicle?.model].filter(Boolean).join(' ')}
              </span>
            </div>
          </div>

          <div className={CARD}>
            <h2 className="text-sm font-bold text-slate-900 mb-3">Line items</h2>
            <div className="space-y-3">
              {(invoice.lineItems || []).map((item, idx) => (
                <div
                  key={`${item.name}-${idx}`}
                  className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-3 text-sm border-b border-slate-100 pb-3 last:border-0 last:pb-0"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900 break-words">{item.name}</p>
                    <p className="text-xs text-slate-500 capitalize">
                      {item.category}
                      {item.paidVia ? ` · ${item.paidVia}` : ''}
                    </p>
                  </div>
                  <p className="font-semibold text-slate-900 shrink-0">{formatINR(item.total)}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100 space-y-1 text-sm">
              <div className="flex justify-between text-slate-600 gap-4">
                <span>Subtotal</span>
                <span className="tabular-nums">{formatINR(invoice.subtotal)}</span>
              </div>
              <div className="flex justify-between text-slate-600 gap-4">
                <span>GST</span>
                <span className="tabular-nums">{formatINR(invoice.gst)}</span>
              </div>
              <div className="flex justify-between font-bold text-slate-900 text-base sm:text-lg pt-2 gap-4">
                <span>Total paid</span>
                <span className="tabular-nums">{formatINR(invoice.totalPaid)}</span>
              </div>
            </div>
          </div>

          {invoice.passportNotice && (
            <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 sm:p-5">
              <div className="flex items-start gap-3">
                <FaPassport className="text-emerald-600 text-lg sm:text-xl shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-emerald-900">{invoice.passportNotice}</p>
                  {invoice.passportLink && (
                    <a
                      href={invoice.passportLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-2 text-sm font-semibold text-emerald-700 underline break-all"
                    >
                      View Passport
                    </a>
                  )}
                  {invoice.passportShareExpiresAt && (
                    <p className="text-xs text-emerald-700/70 mt-1">
                      Link valid until {formatDate(invoice.passportShareExpiresAt)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {(invoice.warrantyCards || []).length > 0 && (
            <div className={CARD}>
              <h2 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
                <FaShieldAlt className="text-emerald-500 shrink-0" /> Warranty cards
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {invoice.warrantyCards.map((w) => (
                  <div key={w.warrantyId} className="rounded-xl bg-slate-50 border border-slate-100 p-3 text-sm">
                    <p className="font-semibold text-slate-900 break-words">{w.partName}</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {w.grade} · Installed {formatDate(w.installDate)} · Expires {formatDate(w.expiresAt)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              type="button"
              disabled={pdfLoading}
              onClick={handleDownloadPdf}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 text-white py-3.5 sm:py-4 text-sm font-bold disabled:opacity-50 w-full"
            >
              {pdfLoading ? <FaSpinner className="animate-spin" /> : <FaDownload />}
              Download PDF
            </button>
            <a
              href={whatsAppShareUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-green-600 text-white py-3.5 sm:py-4 text-sm font-bold w-full"
            >
              <FaWhatsapp /> Share on WhatsApp
            </a>
          </div>
        </main>
      </div>
    );
  }

  /* CV-04 OTP confirmation after payment */
  if (step === 'otp' && deliveryOtp) {
    return (
      <div className={`${PAGE_SHELL} flex flex-col`}>
        <header className={PAGE_HEADER}>
          <div className={PAGE_CONTAINER}>
            <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wide">SparePe</p>
            <h1 className="text-base sm:text-lg font-bold text-slate-900 mt-0.5">Ready for pickup</h1>
          </div>
        </header>
        <main className={`flex-1 flex flex-col items-center justify-center p-4 sm:p-6 ${PAGE_CONTAINER} w-full`}>
          <FaCheckCircle className="text-4xl sm:text-5xl text-emerald-500 mb-4" />
          <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-2 text-center">Payment confirmed</h2>
          <p className="text-slate-600 text-sm text-center mb-6 max-w-sm">
            Show this OTP at the garage to collect your vehicle.
          </p>
          <div className={`${CARD} border-2 border-emerald-300 shadow-lg px-6 sm:px-10 py-6 sm:py-8 text-center w-full max-w-md`}>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">Delivery OTP</p>
            <p className="text-4xl sm:text-5xl font-black tracking-[0.2em] sm:tracking-[0.3em] text-emerald-700 tabular-nums">
              {deliveryOtp.otp}
            </p>
            {deliveryOtp.expiresAt && (
              <p className="text-xs text-slate-400 mt-4">Valid until {formatDate(deliveryOtp.expiresAt)}</p>
            )}
          </div>
          {deliveryOtp.sentToWhatsApp && (
            <p className="text-xs text-emerald-700 mt-4 text-center">Also sent to your WhatsApp</p>
          )}
          {error && (
            <div className="mt-4 rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm px-4 py-3 w-full max-w-md">{error}</div>
          )}
          <button
            type="button"
            disabled={payLoading}
            onClick={handleViewInvoice}
            className="mt-6 sm:mt-8 w-full max-w-md rounded-xl bg-slate-900 text-white py-3.5 sm:py-4 font-bold text-sm sm:text-base flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {payLoading ? <FaSpinner className="animate-spin" /> : <FaFileInvoice />}
            View full invoice
          </button>
        </main>
      </div>
    );
  }

  /* CV-04 — Vehicle ready / collection */
  const labourDue = preview.labourTotal ?? collection?.labourAmount;

  return (
    <div className={`${PAGE_SHELL} pb-32 sm:pb-36`}>
      <header className={PAGE_HEADER}>
        <div className={PAGE_CONTAINER}>
          <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wide">SparePe</p>
          <h1 className="text-base sm:text-lg md:text-xl font-bold text-slate-900 leading-snug mt-0.5">
            Your vehicle is ready for pickup.
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1 break-words">
            {garageLabel} garage · {collection?.vehicle?.registrationNumber || 'Your vehicle'}
          </p>
        </div>
      </header>

      <main className={`${PAGE_CONTAINER} py-4 sm:py-6 space-y-4`}>
        {error && (
          <div className="rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm px-4 py-3">{error}</div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className={CARD}>
            <h2 className="text-sm font-bold text-slate-900 mb-3">Work completed</h2>
            <ul className="space-y-2">
              {(collection?.workCompleted || []).map((line) => (
                <li key={line} className="flex items-start gap-2 text-sm text-slate-700">
                  <FaCheckCircle className="text-emerald-500 shrink-0 mt-0.5" />
                  <span className="break-words">{line}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className={`${CARD} space-y-4`}>
            <h2 className="text-sm font-bold text-slate-900">Invoice preview</h2>

            {(preview.parts || []).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Parts (already paid)</p>
                <div className="space-y-2">
                  {preview.parts.map((p) => (
                    <div key={p.name} className="flex justify-between gap-3 text-sm">
                      <span className="text-slate-700 min-w-0 break-words">{p.name}</span>
                      <span className="font-semibold text-emerald-700 shrink-0 whitespace-nowrap">
                        {formatINR(p.amount)} ✓
                      </span>
                    </div>
                  ))}
                </div>
                {preview.partsPaidTotal > 0 && (
                  <div className="flex justify-between text-sm mt-2 pt-2 border-t border-slate-100 text-slate-600 gap-3">
                    <span>Parts total</span>
                    <span className="tabular-nums shrink-0">{formatINR(preview.partsPaidTotal)}</span>
                  </div>
                )}
              </div>
            )}

            {(preview.labour || []).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Labour</p>
                <div className="space-y-2">
                  {preview.labour.map((li) => (
                    <div key={li.description} className="flex justify-between gap-3 text-sm">
                      <span className="text-slate-700 min-w-0 break-words">{li.description}</span>
                      <span className="font-semibold text-slate-900 tabular-nums shrink-0">{formatINR(li.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="text-sm space-y-1 pt-2 border-t border-slate-100">
              <div className="flex justify-between text-slate-600 gap-3">
                <span>Labour subtotal</span>
                <span className="tabular-nums shrink-0">{formatINR(preview.labourSubtotal)}</span>
              </div>
              <div className="flex justify-between text-slate-600 gap-3">
                <span>GST (18%)</span>
                <span className="tabular-nums shrink-0">{formatINR(preview.labourGst)}</span>
              </div>
              {preview.additionalCharges > 0 && (
                <div className="flex justify-between text-slate-600 gap-3">
                  <span>Additional charges</span>
                  <span className="tabular-nums shrink-0">{formatINR(preview.additionalCharges)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-slate-900 text-base sm:text-lg pt-2 gap-3">
                <span>Labour due now</span>
                <span className="text-red-600 tabular-nums shrink-0">{formatINR(labourDue)}</span>
              </div>
            </div>
          </div>
        </div>

        {onlineMethods.length > 0 && (
          <div className={`${CARD} md:max-w-md`}>
            <p className="text-sm font-semibold text-slate-700 mb-3">Pay labour via</p>
            <div
              className={`grid gap-2 ${
                onlineMethods.length === 1
                  ? 'grid-cols-1'
                  : onlineMethods.length === 2
                    ? 'grid-cols-2'
                    : 'grid-cols-2 sm:grid-cols-3'
              }`}
            >
              {onlineMethods.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setPaymentMethod(m)}
                  className={`rounded-xl py-3 sm:py-3.5 text-sm font-semibold border-2 transition min-h-[44px] ${
                    paymentMethod === m
                      ? 'border-red-500 bg-red-50 text-red-700'
                      : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                  }`}
                >
                  {METHOD_LABELS[m] || m}
                </button>
              ))}
            </div>
          </div>
        )}
      </main>

      <div className={FIXED_FOOTER}>
        <div className={FIXED_FOOTER_INNER}>
          {onlineMethods.length > 0 && (
            <button
              type="button"
              disabled={payLoading || paymentMethod === 'cash'}
              onClick={handlePayLabour}
              className="w-full rounded-xl bg-red-600 disabled:bg-slate-400 text-white py-3.5 sm:py-4 font-bold text-sm sm:text-base shadow-lg min-h-[48px]"
            >
              {payLoading ? (
                <span className="inline-flex items-center gap-2 justify-center">
                  <FaSpinner className="animate-spin" /> Processing…
                </span>
              ) : (
                <>
                  <span className="sm:hidden">Pay & Collect — {formatINR(labourDue)}</span>
                  <span className="hidden sm:inline">Pay Labour & Collect — {formatINR(labourDue)}</span>
                </>
              )}
            </button>
          )}
          {cashSupported && (
            <button
              type="button"
              disabled={payLoading}
              onClick={handleCashAtGarage}
              className="w-full rounded-xl border-2 border-slate-300 bg-white text-slate-800 py-3 sm:py-3.5 font-semibold text-sm min-h-[44px]"
            >
              Pay Cash at Garage
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BridgeCollection;
